<script>
  import { onMount } from 'svelte';
  import { sessionStore } from '../stores';
  import { request } from '../utils/request';
  import { refreshBalance } from '../utils/authSession';
  let nickname = $sessionStore.name || '', coupon = '', balance = null, attendance = null, deletion = null;
  let busy = false, error = '', message = '', earlyChange = null, loadError = '';
  async function load() {
    loadError = '';
    const results = await Promise.allSettled([
      refreshBalance().then(data => balance = data),
      request('/api/attendance_status').then(data => attendance = data),
      request('/api/check_deletion_status').then(data => deletion = data),
      request('/api/profile').then(data => nickname = data.nickname || nickname)
    ]);
    if (results.some(result => result.status === 'rejected')) loadError = '일부 계정 정보를 불러오지 못했습니다.';
  }
  async function perform(action) {
    if (busy) return;
    busy = true; error = ''; message = '';
    try { await action(); } catch (e) { error = e.message; } finally { busy = false; }
  }
  async function saveNickname(pay = false) {
    try {
      const data = await request('/api/profile', { method: 'PATCH', body: { nickname: nickname.trim(), pay_for_early_change: pay } });
      sessionStore.update(s => ({ ...s, name: data.nickname })); earlyChange = null;
      await refreshBalance(); message = '닉네임을 변경했습니다.';
    } catch (e) {
      if (e.data?.code === 'NICKNAME_CHANGE_COOLDOWN') { earlyChange = e.data; return; }
      throw e;
    }
  }
  async function redeem() {
    const data = await request('/api/coupons/redeem', { method: 'POST', body: { code: coupon.trim() } });
    coupon = ''; await load(); message = data.message || '쿠폰을 등록했습니다.';
  }
  async function attend() {
    const data = await request('/api/check_daily_reward', { method: 'POST', body: {} });
    attendance = data; await refreshBalance();
    message = data.bonus_granted ? `출석 보상 ${data.reward_amount || 4} RION을 받았어요.` : '오늘 출석 보상은 이미 받았어요.';
  }
  async function scheduleDeletion() {
    if (!confirm('계정 탈퇴를 신청할까요? 30일 유예 기간이 지나면 계정과 데이터가 삭제됩니다.')) return;
    const data = await request('/api/request_account_deletion', { method: 'POST', body: {} });
    deletion = { scheduled: true, deletion_date: data.deletion_date }; message = '계정 탈퇴가 예약되었습니다.';
  }
  async function cancelDeletion() {
    await request('/api/cancel_account_deletion', { method: 'POST', body: {} }); deletion = { scheduled: false }; message = '탈퇴 예약을 취소했습니다.';
  }
  onMount(() => { load(); });
</script>
<section class="account-benefits">
  <h3>프로필</h3>
  <form on:submit|preventDefault={() => perform(() => saveNickname())}>
    <label>닉네임 <input bind:value={nickname} maxlength="20" required /></label><button disabled={busy} type="submit">변경하기</button>
  </form>
  {#if earlyChange}
    <p>닉네임은 14일마다 무료로 바꿀 수 있어요. 다음 무료 변경: {new Date(earlyChange.next_free_change_at).toLocaleString()}</p>
    <button disabled={busy} on:click={() => perform(() => saveNickname(true))}>{earlyChange.early_change_cost} RION을 사용해 지금 변경</button>
  {/if}
  <h3>RION과 출석</h3>
  {#if balance}<p>영구 RION {balance.permanence_coins ?? 0} · 이벤트 RION {balance.event_coins ?? 0}</p>{/if}
  <p>이벤트 RION은 유효기간이 있으며 먼저 사용됩니다. 앱과 웹에서 잔액을 함께 사용해요.</p>
  <button disabled={busy} on:click={() => perform(attend)}>{attendance?.granted_today ? '오늘 출석 보상 확인' : '오늘 출석 보상 받기'}</button>
  <h3>쿠폰 등록</h3>
  <form on:submit|preventDefault={() => perform(redeem)}><label>쿠폰 코드 <input bind:value={coupon} placeholder="쿠폰 코드를 입력하세요" maxlength="64" required /></label><button disabled={busy || !coupon.trim()} type="submit">등록</button></form>
  <p>주인공 이름은 입력한 표기를 유지합니다.</p>
  <h3>계정 탈퇴</h3>
  {#if deletion?.scheduled}<p>삭제 예정일: {deletion.deletion_date}</p><button disabled={busy} on:click={() => perform(cancelDeletion)}>탈퇴 예약 취소</button>
  {:else}<button disabled={busy || !deletion} on:click={() => perform(scheduleDeletion)}>계정 탈퇴 신청</button>{/if}
  {#if loadError}<p role="alert">{loadError} <button on:click={load}>다시 불러오기</button></p>{/if}
  {#if error}<p role="alert">{error}</p>{/if}{#if message}<p role="status">{message}</p>{/if}
</section>
<style>
  .account-benefits { margin-top: 24px; border-top: 1px solid #8883; } h3 { margin-top: 28px; } p { line-height: 1.7; font-size: .9rem; }
  form { display: flex; flex-wrap: wrap; gap: 12px; align-items: end; } label { display: grid; gap: 8px; }
  input, button { border: 1px solid #8885; background: transparent; color: inherit; padding: 11px 14px; border-radius: 10px; font: inherit; }
  button { cursor: pointer; } button:disabled { opacity: .5; } [role=alert] { color: #c9442f; } [role=status] { color: #3c8557; }
</style>
